import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'

// Types
export interface SearchResult {
  id: string
  type: 'recipe' | 'creator' | 'equipment'
  title: string
  description?: string
  image?: string
  url: string
  metadata?: Record<string, any>
}

export interface SearchFilters {
  types: Array<'recipe' | 'creator' | 'equipment'>
  categories: string[]
  tags: string[]
  difficulty: string[]
  prepTime: {
    min: number | null
    max: number | null
  }
  rating: number | null
  dateRange: {
    start: Date | null
    end: Date | null
  }
}

export interface SearchSuggestion {
  id: string
  text: string
  type: 'query' | 'category' | 'tag' | 'creator'
  count?: number
}

export interface SearchState {
  // Current search
  query: string
  results: SearchResult[]
  suggestions: SearchSuggestion[]
  filters: SearchFilters
  
  // Search state
  loading: boolean
  error: string | null
  
  // Search history
  recentSearches: string[]
  popularSearches: string[]
  
  // Pagination
  pagination: {
    page: number
    limit: number
    total: number
    hasNextPage: boolean
  }
  
  // Search analytics
  analytics: {
    totalResults: number
    searchTime: number
    didYouMean?: string
  }
  
  // Autocomplete
  autocomplete: {
    isEnabled: boolean
    suggestions: string[]
    loading: boolean
  }
}

const initialState: SearchState = {
  query: '',
  results: [],
  suggestions: [],
  filters: {
    types: [],
    categories: [],
    tags: [],
    difficulty: [],
    prepTime: {
      min: null,
      max: null,
    },
    rating: null,
    dateRange: {
      start: null,
      end: null,
    },
  },
  loading: false,
  error: null,
  recentSearches: [],
  popularSearches: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    hasNextPage: false,
  },
  analytics: {
    totalResults: 0,
    searchTime: 0,
  },
  autocomplete: {
    isEnabled: true,
    suggestions: [],
    loading: false,
  },
}

// Async thunks
export const performSearch = createAsyncThunk(
  'search/performSearch',
  async (params: { 
    query: string
    filters?: Partial<SearchFilters>
    page?: number 
  }) => {
    const startTime = Date.now()
    
    // Mock search results
    const mockResults: SearchResult[] = [
      {
        id: '1',
        type: 'recipe',
        title: 'Perfect Pour Over Coffee',
        description: 'A classic pour over recipe with detailed instructions',
        image: '/images/recipes/pour-over.jpg',
        url: '/recipes/perfect-pour-over',
        metadata: {
          author: 'James Hoffmann',
          prepTime: 5,
          totalTime: 10,
          difficulty: 'Medium',
          rating: 4.8,
        },
      },
      {
        id: '2',
        type: 'creator',
        title: 'James Hoffmann',
        description: 'Coffee expert and YouTube creator',
        image: '/images/creators/james.jpg',
        url: '/creators/james-hoffmann',
        metadata: {
          followers: 125000,
          recipes: 156,
          verified: true,
        },
      },
      {
        id: '3',
        type: 'equipment',
        title: 'Hario V60',
        description: 'Classic pour over dripper',
        image: '/images/equipment/v60.jpg',
        url: '/equipment/hario-v60',
        metadata: {
          brand: 'Hario',
          category: 'Pour Over',
          rating: 4.7,
          price: 25,
        },
      },
    ]
    
    const searchTime = Date.now() - startTime
    
    return {
      results: mockResults,
      analytics: {
        totalResults: mockResults.length,
        searchTime,
        didYouMean: params.query.includes('coffe') ? 'coffee' : undefined,
      },
      pagination: {
        page: params.page || 1,
        limit: 20,
        total: mockResults.length,
        hasNextPage: false,
      },
    }
  }
)

export const fetchSuggestions = createAsyncThunk(
  'search/fetchSuggestions',
  async (query: string) => {
    // Mock suggestions
    const mockSuggestions: SearchSuggestion[] = [
      { id: '1', text: 'coffee brewing', type: 'query', count: 1250 },
      { id: '2', text: 'espresso', type: 'category', count: 890 },
      { id: '3', text: 'pour over', type: 'tag', count: 654 },
      { id: '4', text: 'James Hoffmann', type: 'creator', count: 432 },
    ].filter(s => s.text.toLowerCase().includes(query.toLowerCase()))
    
    return mockSuggestions
  }
)

export const fetchAutocomplete = createAsyncThunk(
  'search/fetchAutocomplete',
  async (query: string) => {
    // Mock autocomplete suggestions
    const mockAutocomplete = [
      'coffee brewing techniques',
      'coffee grind size',
      'coffee recipes',
      'coffee equipment',
    ].filter(s => s.toLowerCase().includes(query.toLowerCase()))
    
    return mockAutocomplete
  }
)

export const fetchPopularSearches = createAsyncThunk(
  'search/fetchPopularSearches',
  async () => {
    // Mock popular searches
    return [
      'espresso',
      'pour over',
      'french press',
      'cold brew',
      'coffee grinder',
    ]
  }
)

// Slice
const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload
    },
    clearQuery: (state) => {
      state.query = ''
    },
    setFilters: (state, action: PayloadAction<Partial<SearchFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
    },
    clearResults: (state) => {
      state.results = []
      state.analytics = initialState.analytics
      state.pagination = initialState.pagination
    },
    addRecentSearch: (state, action: PayloadAction<string>) => {
      const query = action.payload.trim()
      if (query && !state.recentSearches.includes(query)) {
        state.recentSearches.unshift(query)
        state.recentSearches = state.recentSearches.slice(0, 10) // Keep only 10 recent searches
      }
    },
    removeRecentSearch: (state, action: PayloadAction<string>) => {
      state.recentSearches = state.recentSearches.filter(q => q !== action.payload)
    },
    clearRecentSearches: (state) => {
      state.recentSearches = []
    },
    clearError: (state) => {
      state.error = null
    },
    setAutocompleteSuggestions: (state, action: PayloadAction<string[]>) => {
      state.autocomplete.suggestions = action.payload
    },
    clearAutocompleteSuggestions: (state) => {
      state.autocomplete.suggestions = []
    },
    toggleAutocomplete: (state) => {
      state.autocomplete.isEnabled = !state.autocomplete.isEnabled
    },
  },
  extraReducers: (builder) => {
    builder
      // Perform search
      .addCase(performSearch.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(performSearch.fulfilled, (state, action) => {
        state.loading = false
        state.results = action.payload.results
        state.analytics = action.payload.analytics
        state.pagination = action.payload.pagination
        
        // Add to recent searches if query is not empty
        if (state.query.trim()) {
          const query = state.query.trim()
          if (!state.recentSearches.includes(query)) {
            state.recentSearches.unshift(query)
            state.recentSearches = state.recentSearches.slice(0, 10)
          }
        }
      })
      .addCase(performSearch.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Search failed'
      })
      
      // Fetch suggestions
      .addCase(fetchSuggestions.fulfilled, (state, action) => {
        state.suggestions = action.payload
      })
      
      // Fetch autocomplete
      .addCase(fetchAutocomplete.pending, (state) => {
        state.autocomplete.loading = true
      })
      .addCase(fetchAutocomplete.fulfilled, (state, action) => {
        state.autocomplete.loading = false
        state.autocomplete.suggestions = action.payload
      })
      .addCase(fetchAutocomplete.rejected, (state) => {
        state.autocomplete.loading = false
      })
      
      // Fetch popular searches
      .addCase(fetchPopularSearches.fulfilled, (state, action) => {
        state.popularSearches = action.payload
      })
  },
})

export const {
  setQuery,
  clearQuery,
  setFilters,
  clearFilters,
  clearResults,
  addRecentSearch,
  removeRecentSearch,
  clearRecentSearches,
  clearError,
  setAutocompleteSuggestions,
  clearAutocompleteSuggestions,
  toggleAutocomplete,
} = searchSlice.actions

export default searchSlice.reducer 